import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isAuthenticated } from '../lib/auth';

export default function HeroSection() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const startCreate = () => {
    if (isAuthenticated()) {
      navigate('/creer-radio/step1');
    } else {
      localStorage.setItem('fromCreateRadio', 'true');
      navigate('/signup');
    }
  };

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-[#1e1b4b] via-[#4c1d95] to-[#6d28d9] text-white">
      {/* Decorative glows */}
      <div className="pointer-events-none absolute -top-24 -start-24 w-96 h-96 rounded-full bg-fuchsia-500/30 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -end-16 w-[28rem] h-[28rem] rounded-full bg-blue-500/30 blur-3xl" />

      <div className="relative max-w-6xl mx-auto px-6 pt-32 pb-24 md:pt-40 md:pb-32 grid md:grid-cols-2 gap-12 items-center">
        {/* Copy */}
        <div className="text-center md:text-start">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider bg-white/10 ring-1 ring-white/20 rounded-full px-3 py-1 mb-6">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            {t('hero.badge')}
          </span>

          <h1 className="text-4xl md:text-6xl font-extrabold leading-tight mb-6">
            {t('hero.title')}{' '}
            <span className="bg-gradient-to-r from-fuchsia-300 to-cyan-300 bg-clip-text text-transparent">
              {t('hero.titleHighlight')}
            </span>
          </h1>

          <p className="text-lg text-purple-100/90 mb-8 max-w-xl mx-auto md:mx-0">
            {t('hero.subtitle')}
          </p>

          <div className="flex flex-wrap justify-center md:justify-start gap-4">
            <button
              onClick={startCreate}
              className="bg-white text-purple-700 font-semibold px-7 py-3.5 rounded-full shadow-lg shadow-purple-900/40 hover:shadow-xl hover:-translate-y-0.5 transition"
            >
              {t('hero.ctaPrimary')}
            </button>
            <button
              onClick={() => navigate('/radios')}
              className="border border-white/40 text-white px-7 py-3.5 rounded-full hover:bg-white/10 transition"
            >
              {t('hero.ctaSecondary')}
            </button>
          </div>

          <p className="text-sm text-purple-200/80 mt-5">{t('hero.trust')}</p>

          <div className="flex flex-wrap justify-center md:justify-start gap-8 mt-10">
            <Stat value="10k+" label={t('hero.stat1')} />
            <Stat value="120+" label={t('hero.stat2')} />
            <Stat value="99.9%" label={t('hero.stat3')} />
          </div>
        </div>

        {/* Visual: stylised player card */}
        <div className="relative hidden md:block">
          <div className="relative mx-auto w-full max-w-sm rounded-3xl bg-white/10 backdrop-blur-xl ring-1 ring-white/20 p-6 shadow-2xl">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-fuchsia-400 to-cyan-400 flex items-center justify-center text-3xl">
                📻
              </div>
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-wider text-emerald-300 font-semibold flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> LIVE
                </div>
                <div className="font-bold text-lg truncate">Waveo FM</div>
                <div className="text-sm text-purple-100/80 truncate">Chillout Session</div>
              </div>
            </div>

            {/* Equalizer bars */}
            <div className="flex items-end gap-1.5 h-16 mt-6">
              {[40, 70, 30, 90, 55, 75, 45, 85, 35, 65, 50, 80].map((h, i) => (
                <span
                  key={i}
                  className="flex-1 rounded-full bg-gradient-to-t from-fuchsia-400 to-cyan-300"
                  style={{ height: `${h}%` }}
                />
              ))}
            </div>

            <div className="mt-6 h-1.5 rounded-full bg-white/20 overflow-hidden">
              <div className="h-full w-2/3 rounded-full bg-gradient-to-r from-fuchsia-300 to-cyan-300" />
            </div>
            <div className="mt-6 flex items-center justify-center gap-6 text-white/90">
              <span className="text-2xl">⏮</span>
              <span className="w-12 h-12 rounded-full bg-white text-purple-700 flex items-center justify-center text-xl shadow-lg">▶</span>
              <span className="text-2xl">⏭</span>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom wave into the white section */}
      <svg className="block w-full -mb-1" viewBox="0 0 1440 120" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
        <path fill="#ffffff" d="M0,64L80,58.7C160,53,320,43,480,48C640,53,800,75,960,80C1120,85,1280,75,1360,69.3L1440,64L1440,120L0,120Z" />
      </svg>
    </section>
  );
}

function Stat({ value, label }) {
  return (
    <div className="text-center md:text-start">
      <div className="text-2xl font-extrabold">{value}</div>
      <div className="text-sm text-purple-200/80">{label}</div>
    </div>
  );
}
